// Text-to-Speech: a VOZ DA CASA primeiro (Edge TTS neural do monólito, a
// mesma dos flashcards — ?a=api.flashcard-tts, cache em disco+BD do lado de
// lá), com Web Speech API só como fallback offline/erro. A voz do navegador
// era robótica demais pra quem depende do leitor (pedido do dono, 2026-08-25).
//
// Autoplay: o clique dá "ativação transitória", mas gerar áudio novo demora
// segundos e a ativação expira — por isso o elemento <audio> é DESTRAVADO
// na hora do clique com um wav silencioso (truque clássico de iOS) e só
// depois recebe o mp3. Cache em memória faz as repetições tocarem na hora.

import { apiBase } from 'es-jogos';

// 1 amostra de silêncio, wav de 58 bytes — só pra destravar o elemento
const WAV_SILENCIO =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

class TTSManagerImpl {
  private enabled = false;
  private voice: SpeechSynthesisVoice | null = null;
  private voicesLoaded = false;
  private audio: HTMLAudioElement | null = null;
  private cache = new Map<string, string>(); // texto -> audio_url
  private pedidoAtual = 0;

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) this.stop();
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isSupported(): boolean {
    return typeof window !== 'undefined'
      && (typeof fetch === 'function' || 'speechSynthesis' in window);
  }

  speak(text: string): void {
    if (!this.enabled || !text.trim()) return;
    this.stop();
    const t = text.trim().slice(0, 400);
    const pedido = ++this.pedidoAtual;

    if (typeof fetch !== 'function') {
      this.speakFallback(t);
      return;
    }

    // Destrava o <audio> DENTRO do gesto do clique (síncrono)
    if (!this.audio) this.audio = new Audio();
    this.audio.src = WAV_SILENCIO;
    this.audio.play().catch(() => { /* só destravando */ });

    void this.buscaEToca(t, pedido);
  }

  private async buscaEToca(texto: string, pedido: number): Promise<void> {
    try {
      let url = this.cache.get(texto);
      if (!url) {
        const r = await fetch(`${apiBase()}/?a=api.flashcard-tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({ texto, voz: 'edge_feminina' }).toString(),
        });
        const data = (await r.json()) as { sucesso?: boolean; audio_url?: string };
        if (data && data.sucesso && data.audio_url) {
          url = data.audio_url.startsWith('/') ? apiBase() + data.audio_url : data.audio_url;
          this.cache.set(texto, url);
        }
      }
      if (pedido !== this.pedidoAtual) return; // clicou de novo no meio: descarta
      if (url && this.audio) {
        this.audio.src = url;
        await this.audio.play();
        return;
      }
    } catch {
      /* sem rede, CORS, rate limit ou autoplay barrado: cai no fallback */
    }
    if (pedido === this.pedidoAtual) this.speakFallback(texto);
  }

  // ---- fallback: Web Speech API (a voz robótica de antes) -----------------

  private temWebSpeech(): boolean {
    return typeof window !== 'undefined' && 'speechSynthesis' in window;
  }

  private loadVoice(): void {
    if (this.voicesLoaded || !this.temWebSpeech()) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return;
    this.voice = voices.find((v) => v.lang === 'pt-BR')
      ?? voices.find((v) => v.lang.startsWith('pt'))
      ?? voices[0]
      ?? null;
    this.voicesLoaded = true;
  }

  private speakFallback(text: string): void {
    if (!this.temWebSpeech()) return;
    this.loadVoice();
    window.speechSynthesis.cancel();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'pt-BR';
    if (this.voice) utter.voice = this.voice;
    utter.rate = 0.95;
    utter.pitch = 1.05;
    window.speechSynthesis.speak(utter);
  }

  stop(): void {
    this.pedidoAtual++;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
    }
    if (this.temWebSpeech()) window.speechSynthesis.cancel();
  }
}

export const ttsManager = new TTSManagerImpl();

// Heurística: pega o texto "lido em voz alta" da página atual.
// Pega título da bar + pergunta/dica/objetivo visível.
export function getReadableActivityText(): string {
  const title = document.querySelector('.activity-bar__title')?.textContent?.trim() ?? '';
  const stage = document.querySelector('.activity-stage');
  if (!stage) return title;
  const selectors = [
    '[class*="__question"]',
    '[class*="__hint"]',
    '[class*="__objective"]',
    '[class*="__instruction"]',
    '[class*="__goal"]',
    '[class*="__dica"]:not([hidden])',
    '[class*="__progress"]',
  ];
  const parts: string[] = [];
  for (const sel of selectors) {
    const el = stage.querySelector<HTMLElement>(sel);
    if (el && el.textContent) parts.push(el.textContent.trim());
  }
  // dedupe + filtrar vazios
  const seen = new Set<string>();
  const unique = [title, ...parts].filter((t) => {
    if (!t) return false;
    if (seen.has(t)) return false;
    seen.add(t);
    return true;
  });
  return unique.join('. ');
}
